import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, Check, AlertCircle, RefreshCw, ArrowRight, Bug, Zap } from 'lucide-react';
import { useSession } from '../hooks/useSession.ts';
import { getUserSubscriptionStatus, getUserScans, getStripeSessionStatus } from '../services/apiService.ts';
// import { useCheckStripeUpgrade } from '../hooks/useCheckStripeUpgrade.ts'; // Alternative hook-based implementation

// Utility function for delays
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export default function UpgradeSuccessScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, loading: authLoading } = useSession();
  const user = session?.user;
  const sessionId = searchParams.get('session_id');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [isPollingComplete, setIsPollingComplete] = useState(false);
  const [backgroundPolling, setBackgroundPolling] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [stripeSessionStatus, setStripeSessionStatus] = useState<any>(null);
  const backgroundIntervalRef = useRef<number | null>(null);
  const hasStartedPollingRef = useRef(false);
  const mountedRef = useRef(true);
  
  const maxRetries = 12; // Increased to allow for manual processing
  const retryDelay = 3000; // 3 seconds between retries

  console.log('[UpgradeSuccessScreen] Component mounted', {
    sessionId,
    userId: user?.id,
    authLoading,
    hasStartedPolling: hasStartedPollingRef.current,
    mounted: mountedRef.current,
  });

  // ✅ Alternative hook-based implementation (commented out):
  // const {
  //   checking,
  //   currentAttempt,
  //   isPollingComplete,
  //   error,
  //   startPolling,
  //   resetState,
  // } = useCheckStripeUpgrade({
  //   maxRetries,
  //   retryDelay,
  //   sessionId,
  //   userId: user?.id,
  // });

  const redirectBasedOnScanHistory = async () => {
    if (!user?.id) {
      console.log('[UpgradeSuccessScreen] No user ID, redirecting to upload');
      navigate('/upload');
      return;
    }

    try {
      console.log('[UpgradeSuccessScreen] Checking scan history for intelligent redirect...');
      const scans = await getUserScans();
      
      if (scans && scans.length > 0) {
        console.log('[UpgradeSuccessScreen] User has existing scans, redirecting to most recent scan', {
          scanCount: scans.length,
          userId: user.id,
          mostRecentScan: scans[0]?.id,
        });
        // Redirect to the most recent scan results
        navigate(`/results/${scans[0].id}`);
      } else {
        console.log('[UpgradeSuccessScreen] New user with no scans, redirecting to upload', {
          userId: user.id,
        });
        navigate('/upload');
      }
    } catch (err) {
      console.error('[UpgradeSuccessScreen] Error checking scan history, defaulting to upload:', err);
      navigate('/upload');
    }
  };

  const checkUpgradeStatus = async (attempt: number = 0): Promise<boolean> => {
    if (!mountedRef.current) {
      console.log('[UpgradeSuccessScreen] Component unmounted, skipping status check');
      return false;
    }

    if (!user?.id) {
      console.log('[UpgradeSuccessScreen] No user ID available, cannot check status');
      return false;
    }

    console.log(`[UpgradeSuccessScreen] Checking upgrade status (attempt ${attempt + 1}/${maxRetries})`, {
      userId: user.id,
      sessionId,
      attempt: attempt + 1,
      mounted: mountedRef.current,
    });
    
    try {
      const status = await getUserSubscriptionStatus();
      
      // ✅ Enhanced debugging - collect detailed status info
      const debugData = {
        attempt: attempt + 1,
        timestamp: new Date().toISOString(),
        userId: user.id,
        sessionId,
        status: {
          isProUser: status.isProUser,
          hasOneTimeScans: status.hasOneTimeScans,
          subscription: status.subscription,
          entitlements: status.entitlements,
        },
        environment: {
          isStripeSandbox: sessionId?.startsWith('cs_test_'),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }
      };
      
      setDebugInfo(debugData);
      
      console.log(`[UpgradeSuccessScreen] Status check result (attempt ${attempt + 1}):`, debugData);
      
      // ✅ If user is upgraded, redirect based on scan history
      if (status.isProUser || status.hasOneTimeScans) {
        console.log('[UpgradeSuccessScreen] ✅ Payment verified successfully, determining redirect destination', {
          isProUser: status.isProUser,
          hasOneTimeScans: status.hasOneTimeScans,
          sessionId,
          userId: user.id,
        });
        
        if (mountedRef.current) {
          setChecking(false);
          setIsPollingComplete(true);
          
          // Brief success display before redirect
          setTimeout(() => {
            if (mountedRef.current) {
              redirectBasedOnScanHistory();
            }
          }, 2000);
        }
        return true;
      }
      
      // ✅ If webhook hasn't processed but we're past attempt 5, check Stripe directly
      if (attempt >= 5 && sessionId && !stripeSessionStatus) {
        console.log('[UpgradeSuccessScreen] 🔍 Webhook may have failed, checking Stripe session directly...');
        try {
          const stripeStatus = await getStripeSessionStatus(sessionId);
          setStripeSessionStatus(stripeStatus);
          
          if (stripeStatus.payment_status === 'paid') {
            console.log('[UpgradeSuccessScreen] ⚡ Stripe session is paid, webhook may have failed');
            console.log('[UpgradeSuccessScreen] Consider refreshing the page or contacting support if the issue persists');
          }
        } catch (stripeError) {
          console.error('[UpgradeSuccessScreen] Error checking Stripe session:', stripeError);
        }
      }
      
      return false;
    } catch (err) {
      console.error(`[UpgradeSuccessScreen] Status check error (attempt ${attempt + 1}):`, {
        error: err,
        sessionId,
        userId: user.id,
        mounted: mountedRef.current,
      });
      
      // ✅ Enhanced error debugging
      const errorDebugData = {
        attempt: attempt + 1,
        timestamp: new Date().toISOString(),
        error: {
          message: (err as Error)?.message,
          name: (err as Error)?.name,
          stack: (err as Error)?.stack,
        },
        sessionId,
        userId: user.id,
      };
      
      setDebugInfo(errorDebugData);
      return false;
    }
  };

  const startBackgroundPolling = () => {
    if (!mountedRef.current) return;
    
    console.log('[UpgradeSuccessScreen] Starting background polling...');
    setBackgroundPolling(true);
    
    backgroundIntervalRef.current = window.setInterval(async () => {
      if (!mountedRef.current) {
        if (backgroundIntervalRef.current) {
          clearInterval(backgroundIntervalRef.current);
          backgroundIntervalRef.current = null;
        }
        return;
      }
      
      console.log('[UpgradeSuccessScreen] Background polling check...');
      const isUpgraded = await checkUpgradeStatus(0); // Reset attempt counter for background polling
      if (isUpgraded && backgroundIntervalRef.current) {
        clearInterval(backgroundIntervalRef.current);
        backgroundIntervalRef.current = null;
        if (mountedRef.current) {
          setBackgroundPolling(false);
        }
      }
    }, 5000); // Check every 5 seconds in background
  };

  const startPolling = async () => {
    if (!mountedRef.current || hasStartedPollingRef.current) {
      console.log('[UpgradeSuccessScreen] Skipping polling - already started or unmounted', {
        mounted: mountedRef.current,
        hasStarted: hasStartedPollingRef.current,
      });
      return;
    }

    hasStartedPollingRef.current = true; // ✅ Prevent multiple polling instances
    
    console.log('[UpgradeSuccessScreen] Starting polling sequence', {
      maxRetries,
      retryDelay,
      sessionId,
      userId: user?.id,
      isStripeSandbox: sessionId?.startsWith('cs_test_'),
    });
    
    if (mountedRef.current) {
      setChecking(true);
      setError(null);
      setIsPollingComplete(false);
    }
    
    // ✅ Use for loop with proper attempt tracking
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (!mountedRef.current) {
        console.log('[UpgradeSuccessScreen] Component unmounted during polling, stopping');
        return;
      }
      
      setCurrentAttempt(attempt);
      
      const isUpgraded = await checkUpgradeStatus(attempt);
      if (isUpgraded) {
        return; // Success - exit early
      }
      
      // If not the last attempt, wait before next retry
      if (attempt < maxRetries - 1 && mountedRef.current) {
        console.log(`[UpgradeSuccessScreen] Waiting ${retryDelay}ms before next attempt...`, {
          currentAttempt: attempt + 1,
          nextAttempt: attempt + 2,
          sessionId,
        });
        await delay(retryDelay);
      }
    }
    
    // ✅ Max retries reached - show fallback UI but continue polling in background
    if (mountedRef.current) {
      console.log('[UpgradeSuccessScreen] ❌ Max retries reached, showing fallback UI and starting background polling', {
        maxRetries,
        sessionId,
        userId: user?.id,
        isStripeSandbox: sessionId?.startsWith('cs_test_'),
      });
      setChecking(false);
      setError('max_retries_reached');
      startBackgroundPolling();
    }
  };

  const handleRetryCheck = () => {
    if (!mountedRef.current) return;
    
    console.log('[UpgradeSuccessScreen] Manual retry triggered by user', {
      sessionId,
      userId: user?.id,
    });
    
    // Clear background polling if active
    if (backgroundIntervalRef.current) {
      clearInterval(backgroundIntervalRef.current);
      backgroundIntervalRef.current = null;
      setBackgroundPolling(false);
    }
    
    // Reset polling state
    hasStartedPollingRef.current = false;
    setCurrentAttempt(0);
    setDebugInfo(null); // Clear previous debug info
    setStripeSessionStatus(null); // Clear Stripe status
    startPolling();
  };

  const handleContinueAsFree = () => {
    console.log('[UpgradeSuccessScreen] User chose to continue as free user', {
      sessionId,
      userId: user?.id,
    });
    
    // Clear background polling if active
    if (backgroundIntervalRef.current) {
      clearInterval(backgroundIntervalRef.current);
      backgroundIntervalRef.current = null;
      setBackgroundPolling(false);
    }
    
    navigate('/upload');
  };

  const toggleDebugInfo = () => {
    setShowDebugInfo(!showDebugInfo);
  };

  useEffect(() => {
    // ✅ Wait for useSession hook to be hydrated before starting checks
    if (authLoading) {
      console.log('[UpgradeSuccessScreen] Waiting for auth to load...');
      return;
    }
    
    if (!user?.id) {
      console.log('[UpgradeSuccessScreen] No user session, redirecting to login');
      navigate('/login');
      return;
    }

    // Only start polling once when user is confirmed and component is mounted
    if (!hasStartedPollingRef.current && mountedRef.current) {
      console.log('[UpgradeSuccessScreen] User session confirmed, starting polling', {
        userId: user.id,
        sessionId,
        isStripeSandbox: sessionId?.startsWith('cs_test_'),
      });
      startPolling();
    }
  }, [user?.id, authLoading, navigate, sessionId]);

  // ✅ Enhanced cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      console.log('[UpgradeSuccessScreen] Component unmounting, cleaning up...');
      mountedRef.current = false;
      hasStartedPollingRef.current = false;
      
      if (backgroundIntervalRef.current) {
        clearInterval(backgroundIntervalRef.current);
        backgroundIntervalRef.current = null;
      }
    };
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user?.id) {
    return null; // Will redirect to login
  }

  // ✅ Show success state when payment is verified
  if (isPollingComplete && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Payment Successful! 🎉
            </h1>
            
            <p className="text-gray-600 mb-6">
              Your upgrade has been processed successfully. Redirecting you to the right place...
            </p>
            
            <div className="flex items-center justify-center space-x-2 text-blue-600">
              <Loader className="w-4 h-4 animate-spin" />
              <span className="text-sm">Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Show verification in progress with improved messaging
  if (checking) {
    const progressPercentage = ((currentAttempt + 1) / maxRetries) * 100;
    const isLongWait = currentAttempt >= 5;
    const isStripeSandbox = sessionId?.startsWith('cs_test_');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Verifying Payment...
            </h1>
            
            <div className="text-gray-600 mb-6 space-y-2">
              <p>We're confirming your payment and setting up your account.</p>
              {isStripeSandbox && (
                <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                  🧪 Sandbox Mode: Webhooks may take longer to process
                </p>
              )}
              {stripeSessionStatus && (
                <p className="text-sm text-green-600 bg-green-50 p-2 rounded flex items-center justify-center space-x-1">
                  <Zap className="w-4 h-4" />
                  <span>Payment confirmed via Stripe - processing upgrade...</span>
                </p>
              )}
              {isLongWait && (
                <p className="text-sm text-orange-600">
                  Still upgrading your account… This may take up to 1 minute.
                </p>
              )}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-500">Attempt:</span>
                <span className="font-medium">{currentAttempt + 1} of {maxRetries}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex items-center justify-center mt-3 space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
            
            {sessionId && (
              <div className="text-xs text-gray-400 mt-4">
                <p>Session ID for Support:</p>
                <p className="font-mono break-all">{sessionId}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ✅ Enhanced fallback UI with debugging capabilities
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-orange-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Finalizing Your Upgrade
          </h1>
          
          <div className="text-gray-600 mb-6 space-y-2">
            <p>We're still finalizing your upgrade. This can take up to a minute.</p>
            {sessionId?.startsWith('cs_test_') && (
              <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                🧪 Sandbox Mode: Test payments may have webhook delays
              </p>
            )}
            {stripeSessionStatus && (
              <p className="text-sm text-green-600 bg-green-50 p-2 rounded flex items-center justify-center space-x-1">
                <Zap className="w-4 h-4" />
                <span>Payment confirmed - upgrade processing...</span>
              </p>
            )}
            {backgroundPolling && (
              <div className="flex items-center justify-center space-x-2 text-blue-600">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                <span className="text-sm">Checking in background...</span>
              </div>
            )}
            <p className="text-sm text-gray-500">You can retry or contact support if needed.</p>
          </div>
          
          <div className="space-y-3 mb-6">
            <button
              onClick={handleRetryCheck}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry Check</span>
            </button>
            
            <button
              onClick={handleContinueAsFree}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
            >
              <ArrowRight className="w-4 h-4" />
              <span>Continue as Free User</span>
            </button>
            
            {debugInfo && (
              <button
                onClick={toggleDebugInfo}
                className="w-full bg-gray-50 text-gray-600 py-2 px-4 rounded-lg text-sm hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2"
              >
                <Bug className="w-4 h-4" />
                <span>{showDebugInfo ? 'Hide' : 'Show'} Debug Info</span>
              </button>
            )}
          </div>
          
          {showDebugInfo && debugInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm font-medium text-gray-700 mb-2">Debug Information:</p>
              <pre className="text-xs text-gray-600 overflow-auto max-h-40">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
              {stripeSessionStatus && (
                <>
                  <p className="text-sm font-medium text-gray-700 mb-2 mt-4">Stripe Session Status:</p>
                  <pre className="text-xs text-gray-600 overflow-auto max-h-40">
                    {JSON.stringify(stripeSessionStatus, null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}
          
          {sessionId && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Session ID for Support:</p>
              <p className="text-xs font-mono text-gray-800 break-all">{sessionId}</p>
            </div>
          )}
          
          <div className="text-xs text-gray-500">
            <p className="mb-2">Need help?</p>
            <p>Contact support with your Session ID above for faster assistance.</p>
          </div>
        </div>
      </div>
    </div>
  );
} 